import { useEffect, useState, useCallback, useRef } from "react";
import { Gauge, Save, Loader2, RefreshCw, ShieldCheck, ShieldX, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExpedienteDigital } from "@/components/common/ExpedienteDigital";
import { RiesgoEBRBreakdown } from "@/components/riesgo/RiesgoEBRBreakdown";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NIVELES = [{ v: "bajo", l: "Bajo" }, { v: "medio", l: "Medio" }, { v: "alto", l: "Alto" }];

export function GradoRiesgo({ clienteId, cliente, onChange }) {
  const { user } = useAuth();
  const esOC = ["super_admin", "oficial_cumplimiento"].includes(user?.role);
  const puedeAjustar = ["oficial_cumplimiento", "analista_pld", "admin_entidad"].includes(user?.role);
  const [info, setInfo] = useState(null);
  const [resp, setResp] = useState({});
  const [saving, setSaving] = useState(false);
  const [justificacion, setJustificacion] = useState("");
  const [showAjuste, setShowAjuste] = useState(false);
  // override
  const [cartas, setCartas] = useState([]);
  const [cartaId, setCartaId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [comite, setComite] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  // Feb-2026 (Disp. 43ª): subida inline de la carta de autorización sin salir del bloque
  // de override. Reusa POST /documentos/upload con tipo/categoría preseleccionados.
  const [subiendoCarta, setSubiendoCarta] = useState(false);
  const cartaFileRef = useRef(null);

  const load = useCallback(async () => {
    const data = await api.get(`/riesgo/cedula/${clienteId}`).then((r) => r.data);
    setInfo(data);
    setResp(data.cedula?.respuestas || {});
    const docs = await api.get(`/documentos`, { params: { cliente_id: clienteId, categoria: "override" } }).then((r) => r.data);
    // Orden descendente por fecha de carga: la carta más reciente arriba, para que el OC
    // no tenga que rastrearla entre pruebas anteriores.
    const ordenadas = docs
      .filter((d) => d.archivo_key)
      .sort((a, b) => String(b.fecha_carga || b.created_at || "").localeCompare(String(a.fecha_carga || a.created_at || "")));
    setCartas(ordenadas);
  }, [clienteId]);

  useEffect(() => { if (clienteId) load(); }, [clienteId, load]);

  const setNivel = (fc, sc, v) => setResp((p) => ({ ...p, [fc]: { ...(p[fc] || {}), [sc]: v } }));

  const guardar = async () => {
    if (justificacion.trim().length < 10) { toast.error("El ajuste manual requiere una justificación escrita (mínimo 10 caracteres)."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/riesgo/cedula", { cliente_id: clienteId, respuestas: resp, justificacion });
      toast.success(`Ajuste manual aplicado: grado ${data.cedula.grado} (${data.cedula.puntaje_total} pts)`);
      setJustificacion(""); setShowAjuste(false);
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const autorizar = async () => {
    if (!cartaId) { toast.error("Adjunta la carta PDF con 'Cargar carta de autorización' o selecciona una ya cargada."); return; }
    setAuthBusy(true);
    try {
      await api.post("/riesgo/override", { cliente_id: clienteId, motivo, carta_doc_id: cartaId, comite_vobo: comite });
      toast.success("Operación autorizada (override). Cliente liberado para operar.");
      await load(); onChange && onChange();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAuthBusy(false); }
  };

  // Feb-2026: subida inline de la carta con tipo/categoría preseleccionados. El OC ya no
  // elige tipo (queda fijo en "Carta de autorización (override)"), y al terminar la nueva
  // carta queda auto-seleccionada en el <Select> — sin salir del bloque de override.
  const subirCarta = async (file) => {
    if (!file) return;
    if (!/pdf/i.test(file.type || "") && !/\.pdf$/i.test(file.name || "")) {
      toast.error("La carta de autorización debe ser un archivo PDF."); return;
    }
    setSubiendoCarta(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", "Carta de autorización (override)");
      fd.append("categoria", "override");
      fd.append("cliente_id", clienteId);
      fd.append("clasificacion", "Soporte");
      const { data } = await api.post("/documentos/upload", fd);
      toast.success("Carta cargada y cifrada. Ya está seleccionada para autorizar.");
      // Auto-selecciona la carta recién subida sin esperar el reload completo.
      if (data?.id) {
        setCartas((prev) => {
          const yaEsta = prev.some((d) => d.id === data.id);
          if (yaEsta) return prev;
          return [data, ...prev];
        });
        setCartaId(data.id);
      }
      await load();
      if (cartaFileRef.current) cartaFileRef.current.value = "";
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubiendoCarta(false); }
  };

  if (!info) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const c = info.cliente;
  const ced = info.cedula;

  return (
    <div data-testid="grado-riesgo">
      {/* feb-2026 — eliminado el header duplicado del semáforo (antes convivía con el
          semáforo del RiesgoEBRBreakdown embebido, sobre el mismo dato). El breakdown
          ya pinta el semáforo consolidado con la metodología vigente. Aquí solo se
          conserva la caja de "Origen del cálculo" + recálculo, y el enlace al ajuste
          manual excepcional para el OC. */}
      <p className="mb-4 rounded-md bg-slate-50 p-2.5 text-[11px] text-slate-500">{info.fundamento}</p>

      {/* Origen del cálculo. Nota: el botón "Recalcular" del breakdown de abajo cubre la
          acción; aquí solo dejamos la evidencia textual (versión de metodología y umbrales). */}
      <div className="mb-4 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600" data-testid="riesgo-origen-box">
        <span className="font-medium text-slate-800">Cálculo {c.riesgo_origen === "ajuste_manual" ? "ajustado manualmente por el OC" : "automático (EBR)"}</span>
        {c.metodologia_version && <span> · metodología v{c.metodologia_version}</span>}
        {c.umbrales_aplicados && <span> · umbrales medio≥{c.umbrales_aplicados.medio} / alto≥{c.umbrales_aplicados.alto}</span>}
        <p className="mt-0.5 text-[11px] text-slate-400">El grado se deriva automáticamente del expediente contra los catálogos de la entidad. El OC puede recalcularlo contra la metodología vigente desde el desglose.</p>
      </div>

      {/* Desglose CONSOLIDADO del EBR (5 factores + presencia de dato). Fuente única:
          GET /api/riesgo/perfil-ebr/{cliente_id}?tipo=cliente. Ya incluye su propio
          semáforo consolidado y su botón "Recalcular con metodología vigente" (para OC). */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
          Grado EBR consolidado — los 5 factores
        </p>
        <RiesgoEBRBreakdown docId={clienteId} tipo="cliente" canRecalcular={esOC} onRecalculado={() => { load(); onChange && onChange(); }} />
      </div>

      {/* Ajuste manual EXCEPCIONAL (override del cálculo automático) */}
      {puedeAjustar && (
        <div className="mb-4">
          {!showAjuste ? (
            <Button data-testid="abrir-ajuste-manual-btn" size="sm" variant="ghost" className="text-slate-600" onClick={() => setShowAjuste(true)}>
              <Gauge className="mr-2 h-4 w-4 text-primary" /> Ajustar manualmente el grado (excepcional, requiere justificación)
            </Button>
          ) : (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="ajuste-manual-box">
              <p className="mb-1 flex items-center gap-2 text-sm font-medium text-amber-800"><Gauge className="h-4 w-4" /> Ajuste manual excepcional de la cédula EBR</p>
              <p className="mb-3 text-[11px] text-amber-700">El default del sistema es el cálculo automático. Este ajuste sobrescribe el grado y queda en bitácora con tu justificación.</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {info.metodologia.factores.map((f) => (
                  <div key={f.clave} className="rounded-md border border-slate-200 bg-white p-3">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{f.nombre} ({f.ponderacion}%)</p>
                    <div className="space-y-2">
                      {f.subvariables.map((s) => (
                        <div key={s.clave} className="flex items-center justify-between gap-2">
                          <Label className="text-xs text-slate-600">{s.nombre}</Label>
                          <Select value={(resp[f.clave] || {})[s.clave] || "bajo"} onValueChange={(v) => setNivel(f.clave, s.clave, v)}>
                            <SelectTrigger data-testid={`ced-${f.clave}-${s.clave}`} className="h-8 w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>{NIVELES.map((nv) => <SelectItem key={nv.v} value={nv.v}>{nv.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Label className="mb-1 block text-xs text-slate-600">Justificación del ajuste (obligatoria)</Label>
                <Textarea data-testid="ajuste-justificacion" rows={2} value={justificacion} onChange={(e) => setJustificacion(e.target.value)} placeholder="Sustento documentado del ajuste manual del grado de riesgo…" />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button data-testid="ajuste-cancelar-btn" size="sm" variant="ghost" onClick={() => { setShowAjuste(false); setJustificacion(""); }}>Cancelar</Button>
                <Button data-testid="cedula-guardar-btn" size="sm" onClick={guardar} disabled={saving} className="bg-primary hover:bg-sidebar-hover">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Aplicar ajuste manual
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Override */}
      {c.semaforo === "rojo" && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="mb-2 text-sm font-semibold text-slate-900">Autorización de la mesa (override)</p>
          {c.bloqueado_lpb ? (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="override-lpb-bloqueo">
              <ShieldX className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                No procede override: coincidencia CONFIRMADA en{" "}
                <b data-testid="override-bloqueo-lista">
                  {c.bloqueo_lista_label || c.bloqueo_lista || "lista de sanción"}
                </b>
                {c.bloqueo_score ? ` (score ${c.bloqueo_score})` : ""}. El cliente permanece bloqueado (comité, dispersión y pago a tercero impedidos); únicamente se reporta.
              </span>
            </div>
          ) : c.override_autorizado ? (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="override-autorizado">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              Operación autorizada por el Oficial de Cumplimiento. El cliente puede operar bajo debida diligencia reforzada.
            </div>
          ) : esOC ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Para liberar la operación se requiere cargar la carta de autorización en PDF. Sin carta cargada, no se libera.</p>
              {/* Feb-2026 (Disp. 43ª): input inline dedicado a la carta de autorización.
                  Reusa POST /documentos/upload con tipo_documento y categoria fijos, para
                  eliminar la fricción del checklist KYC y el picker de tipos genérico. */}
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="override-carta-cargar-box">
                <Label className="mb-1 block text-xs font-medium text-slate-700">Cargar carta de autorización (PDF)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    ref={cartaFileRef}
                    data-testid="override-carta-file-input"
                    type="file"
                    accept="application/pdf,.pdf"
                    disabled={subiendoCarta}
                    onChange={(e) => subirCarta(e.target.files?.[0] || null)}
                    className="h-9"
                  />
                  {subiendoCarta && (
                    <span className="flex items-center gap-1 text-xs text-slate-500" data-testid="override-carta-upload-busy">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Subiendo…
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-slate-500">Solo PDF (máx. 15 MB). Al terminar, la carta queda seleccionada para autorizar.</p>
              </div>
              <ExpedienteDigital clienteId={clienteId} cliente={cliente} categoria="override" onChange={load} />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <Label className="mb-1 block text-xs">Carta de autorización (PDF)</Label>
                  <Select value={cartaId} onValueChange={setCartaId}>
                    <SelectTrigger data-testid="override-carta-select"><SelectValue placeholder="Selecciona carta cargada" /></SelectTrigger>
                    <SelectContent>{cartas.map((d) => <SelectItem key={d.id} value={d.id}>{d.nombre_archivo}{d.fecha_carga ? ` · ${d.fecha_carga}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1 block text-xs">V.B. Comité de Comunicación y Control (opcional)</Label>
                  <Input data-testid="override-comite" value={comite} onChange={(e) => setComite(e.target.value)} placeholder="Comité de Comunicación y Control 2026-06" />
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Motivo / sustento de la autorización</Label>
                <Textarea data-testid="override-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              </div>
              <div className="flex justify-end">
                <Button data-testid="override-autorizar-btn" onClick={autorizar} disabled={authBusy} className="bg-red-600 hover:bg-red-700 text-white">
                  {authBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Autorizar operación
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-amber-600">Cliente en grado ALTO. Solo el Oficial de Cumplimiento puede autorizar la operación (override con carta PDF).</p>
          )}
        </div>
      )}
    </div>
  );
}
